import React from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Spinner, Modal, ModalHeader, ModalBody, Row, Col } from 'reactstrap';
import {
  useAppContext,
  useTranslator,
  FormikSelect as Select,
  FormikInput as Input,
  FormikPasswordInput as PasswordInput,
  FormikCheckboxInput as CheckboxInput,
  useToast,
} from '@front-activerse/common-components';
import { validationSchema, validationSchema2 } from './validationSchema';
import { setAuthCredentials } from '@front-activerse/common-components';
import { useState } from 'react';

interface ISignupAndActivateProductForm {
  registerAndActivateCoupon: (request: any) => Promise<any>;
  userDataOnDemand: () => Promise<unknown>;
}

const SignupAndActivateProductForm: React.FC<ISignupAndActivateProductForm> = ({
  registerAndActivateCoupon,
  userDataOnDemand,
}) => {
  const { t } = useTranslator();
  const [userDataLoading, setUserDataLoading] = useState(false);

  const {
    images: { eye, eyeSlash },
    consts: { APP_BRAND },
  } = useAppContext();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [authToken, setAuthToken] = useState('');

  const initialValues = {
    sex: 'female',
    email: '',
    password: '',
    name: '',
    accessCode: '',
    agreementsTermsAndConditions: false,
    agreementsPersonalDataProcessing: false,
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setFieldError }: FormikHelpers<typeof initialValues>
  ) => {
    const request = {
      ...values,
      agreementsPersonalDataProcessing: true, // required by backend
    };

    try {
      const response = await registerAndActivateCoupon(request);
      const { details, __typename, token, existingAccountAutoLogged } = response.data.auth.register;

      if (__typename === 'ValidationException') {
        details.forEach((detail: { fieldName: string; messages: string[] }) =>
          setFieldError(detail.fieldName, detail.messages[0])
        );
      } else if (__typename === 'AuthRegisterSuccess' && !existingAccountAutoLogged) {
        toastSuccess(<p>{t('toastMessage.couponActivated')}</p>, 5000);
        setAuthCredentials(token);

        const response2 = await userDataOnDemand();

        if (response2) {
          if (APP_BRAND === 'dtba') {
            navigate('/');
          } else {
            navigate('/thankyou-activation');
          }
        }
      } else if (__typename === 'AuthRegisterSuccess' && existingAccountAutoLogged) {
        setAuthToken(token);
        setShowModal(true);
      } else {
        toastError();
      }
    } catch (err) {
      toastError();
    }
  };

  const toggle = () => {
    setShowModal(!showModal);
  };

  const handleClick = async () => {
    setAuthCredentials(authToken);
    try {
      setUserDataLoading(true);
      const response2 = await userDataOnDemand();

      if (response2) {
        navigate('/');
      }
    } catch (err) {
      toastError();
    } finally {
      setUserDataLoading(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={
          APP_BRAND === 'hashimotoPl' || APP_BRAND === 'diabetesPl'
            ? validationSchema2
            : validationSchema
        }
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <Input name="email" type="email" label={t('inputLabel.emailAddress')} translateErrMsg />
            <PasswordInput
              name="password"
              label={t('inputLabel.password')}
              iconEye={eye}
              iconEyeSlash={eyeSlash}
              translateErrMsg
            />
            <Input name="name" type="text" label={t('inputLabel.name')} translateErrMsg />
            <Select name="sex" label={t('inputLabel.gender')} inline={false}>
              <option value="female">{t('inputGender.optionFemale')}</option>
              <option value="male">{t('inputGender.optionMale')}</option>
            </Select>
            <Input
              name="accessCode"
              type="text"
              label={t('inputLabel.accessCode')}
              translateErrMsg
            />
            <CheckboxInput
              name="agreementsTermsAndConditions"
              translateErrMsg
              htmlFor="agreementsTermsAndConditions"
              checkbox
            >
              <>
                {t('app.signUp.termsAccept.checkbox.part1')}{' '}
                <Link to={'/terms-of-service'}>
                  {t('app.signUp.termsAccept.checkbox.termsOfServiceLink')}
                </Link>{' '}
                {t('app.signUp.termsAccept.checkbox.part2')}{' '}
                <Link to={'/privacy-policy'}>
                  {t('app.signUp.termsAccept.checkbox.privacyPolicyLink')}
                </Link>{' '}
                {t('app.signUp.termsAccept.checkbox.part3')}
              </>
            </CheckboxInput>

            {(APP_BRAND === 'hashimotoPl' || APP_BRAND === 'diabetesPl') && (
              <CheckboxInput
                name="agreementsPersonalDataProcessing"
                translateErrMsg
                htmlFor="agreementsPersonalDataProcessing"
                checkbox
              >
                {t('signUp.termsAccept2.checkbox')}
              </CheckboxInput>
            )}

            <Button
              type="submit"
              color="primary"
              className="w-100 mt-xl"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner />
                  <span className="ms-2">{t('data-processing')}</span>
                </>
              ) : (
                t('couponActivation.activateAccess.button')
              )}
            </Button>
          </Form>
        )}
      </Formik>
      <Modal className="auth-modal" isOpen={showModal} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle} />
        <ModalBody>
          <p className="large mb-xl">{t('existingAccountPopup.content')}</p>
          <Row>
            <Col>
              <Button color="outline-primary" className="w-100" onClick={toggle}>
                {t('existingAccountPopup.cancelButton')}
              </Button>
            </Col>
            <Col>
              <Button
                color="primary"
                className="w-100 mt-3"
                onClick={handleClick}
                disabled={userDataLoading}
              >
                {userDataLoading ? (
                  <>
                    <Spinner />
                    <span className="ms-2">{t('data-processing')}</span>
                  </>
                ) : (
                  t('existingAccountPopup.loginButton')
                )}
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
};

export default SignupAndActivateProductForm;
